import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { mockData } from '../mock';
import { CheckCircle2, TrendingUp } from 'lucide-react';
import { ResellerModal } from './ResellerModal';

export const Resellers = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
    <section id="resellers" ref={containerRef} className="relative py-32 px-6 bg-[#0a1628] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://images.unsplash.com/photo-1578323851363-cf6a1a6afbb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxmcmVzaCUyMGljZXxlbnwwfHx8fDE3ODUyMDUyOTV8MA&ixlib=rb-4.1.0&q=85"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#0a1628]/90 to-[#0a1628]" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#7dd3fc]/10 rounded-full mb-6"
          >
            <TrendingUp className="text-[#7dd3fc]" size={20} />
            <span className="text-[#7dd3fc] font-semibold">Oportunidade de Negócio</span>
          </motion.div>

          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            {mockData.resellers.title}
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Faça parte da nossa rede de parceiros e construa um negócio lucrativo com produtos de qualidade premium
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Benefits List */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-6"
          >
            {mockData.resellers.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                whileHover={{ x: 10 }}
                className="flex items-start gap-4 group"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-[#7dd3fc]/10 rounded-xl flex items-center justify-center group-hover:bg-[#7dd3fc]/20 transition-colors duration-300">
                  <CheckCircle2 className="text-[#7dd3fc]" size={24} />
                </div>
                <div>
                  <p className="text-lg text-white group-hover:text-[#7dd3fc] transition-colors duration-300">
                    {benefit}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative p-12 bg-gradient-to-br from-[#7dd3fc]/20 via-[#7dd3fc]/10 to-transparent backdrop-blur-xl rounded-3xl border border-[#7dd3fc]/30 overflow-hidden"
          >
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#7dd3fc]/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#7dd3fc]/10 rounded-full blur-2xl" />

            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-white mb-6">
                Pronto para começar?
              </h3>
              
              <p className="text-white/70 mb-8 leading-relaxed">
                Entre em contato conosco e receba uma proposta comercial personalizada. Nossa equipe está pronta para ajudar você a crescer.
              </p>

              <motion.button
                onClick={() => setModalOpen(true)}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                className="w-full py-5 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold text-lg rounded-xl shadow-xl shadow-[#7dd3fc]/30 hover:shadow-2xl hover:shadow-[#7dd3fc]/40 transition-all duration-300"
                data-testid="reseller-cta-btn"
              >
                {mockData.resellers.cta}
              </motion.button>

              <p className="text-center text-white/50 text-sm mt-6">
                Resposta em até 24 horas
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
    <ResellerModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};
